import { useState } from "react";
import { cn } from "@/lib/utils";
import { OpportunityCard } from "./OpportunityCard";
import { STATUS_ACCENT, STATUS_LABEL, STATUS_ORDER } from "./opportunityConstants";
import type { Opportunity, OpportunityStatus } from "./opportunityTypes";

interface OpportunityBoardProps {
  opportunities: Opportunity[];
  onOpen: (o: Opportunity) => void;
  onStatusChange: (id: string, status: OpportunityStatus) => void;
}

export function OpportunityBoard({
  opportunities,
  onOpen,
  onStatusChange,
}: OpportunityBoardProps) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverStatus, setHoverStatus] = useState<OpportunityStatus | null>(null);

  const byStatus = (status: OpportunityStatus) =>
    opportunities.filter((o) => o.status === status);

  return (
    <div className="flex gap-3 overflow-x-auto pb-4">
      {STATUS_ORDER.map((status) => {
        const items = byStatus(status);
        const isHovered = hoverStatus === status;

        return (
          <section
            key={status}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              setHoverStatus(status);
            }}
            onDragLeave={() => setHoverStatus((s) => (s === status ? null : s))}
            onDrop={(e) => {
              e.preventDefault();
              const id = e.dataTransfer.getData("text/plain") || draggingId;
              setHoverStatus(null);
              setDraggingId(null);
              if (!id) return;
              const current = opportunities.find((o) => o.id === id);
              if (current && current.status !== status) {
                onStatusChange(id, status);
              }
            }}
            className={cn(
              "flex w-[260px] shrink-0 flex-col rounded-lg border bg-muted/30 transition",
              isHovered && "border-primary bg-primary/5",
            )}
            aria-label={`${STATUS_LABEL[status]} column`}
          >
            <header className="flex items-center gap-2 border-b px-3 py-2">
              <span className={cn("h-2 w-2 shrink-0 rounded-full", STATUS_ACCENT[status])} />
              <h3 className="text-sm font-semibold">{STATUS_LABEL[status]}</h3>
              <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                {items.length}
              </span>
            </header>

            <div className="flex flex-1 flex-col gap-2 p-2">
              {items.length === 0 ? (
                <p className="px-1 py-4 text-center text-xs text-muted-foreground">
                  {isHovered ? "Drop here" : "Empty"}
                </p>
              ) : (
                items.map((o) => (
                  <OpportunityCard
                    key={o.id}
                    opportunity={o}
                    onOpen={onOpen}
                    onDragStart={(item) => setDraggingId(item.id)}
                    onDragEnd={() => {
                      setDraggingId(null);
                      setHoverStatus(null);
                    }}
                    isDragging={draggingId === o.id}
                  />
                ))
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
